import { useState, useEffect, useCallback } from 'react'
import { Alert, Box, Button, CircularProgress, Snackbar, Typography } from '@mui/material'
import { Add, BugReport } from '@mui/icons-material'
import { useAuth } from '../contexts/auth-context'
import { useIssues } from '../hooks/use-issues'
import { supabase } from '../lib/supabase'
import { PageContainer } from '../components/PageContainer'
import { IssueCard } from '../components/issues/IssueCard'
import { IssueFilterBar } from '../components/issues/IssueFilterBar'
import { CreateIssueDialog } from '../components/issues/CreateIssueDialog'

const EMPTY_FILTERS = { search: '', status: '', priority: '', customer: '' }

export function IssuesPage() {
	const { customerId } = useAuth()
	const { issues, loading, error, createIssue, updateIssue, getAttachmentUrl } = useIssues()
	const [showCreateDialog, setShowCreateDialog] = useState(false)
	const [createLoading, setCreateLoading] = useState(false)
	const [sensors, setSensors] = useState([])
	const [expandedIssue, setExpandedIssue] = useState(null)
	const [filters, setFilters] = useState(EMPTY_FILTERS)
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

	const showSnackbar = useCallback((message, severity = 'success') => {
		setSnackbar({ open: true, message, severity })
	}, [])

	useEffect(() => {
		if (!customerId || !showCreateDialog) return

		const fetchSensors = async () => {
			const { data } = await supabase
				.from('sensor_configs')
				.select('sensor_id, sensor_location')
				.eq('customer_id', customerId)
				.order('sensor_location')
			setSensors(data || [])
		}
		fetchSensors()
	}, [customerId, showCreateDialog])

	const filteredIssues = issues.filter((issue) => {
		const search = filters.search.toLowerCase()
		return (
			(!search || issue.title.toLowerCase().includes(search)) &&
			(!filters.status || issue.status === filters.status) &&
			(!filters.priority || issue.priority === filters.priority)
		)
	})

	const handleCreateIssue = async (data, files) => {
		try {
			setCreateLoading(true)
			await createIssue(data, files)
			setShowCreateDialog(false)
			showSnackbar('Issue reported successfully')
		} catch (err) {
			showSnackbar(err.message || 'Failed to create issue', 'error')
		} finally {
			setCreateLoading(false)
		}
	}

	const handleCloseIssue = async (issueId) => {
		try {
			await updateIssue(issueId, { status: 'closed' })
			showSnackbar('Issue closed')
		} catch (err) {
			showSnackbar(err.message || 'Failed to close issue', 'error')
		}
	}

	if (loading) {
		return (
			<PageContainer>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
					<CircularProgress />
				</Box>
			</PageContainer>
		)
	}

	if (error) {
		return (
			<PageContainer>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
					<Typography color="error">Error loading issues: {error}</Typography>
				</Box>
			</PageContainer>
		)
	}

	return (
		<PageContainer>
			<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
				<Box>
					<Typography variant="h4" fontWeight="bold" gutterBottom>
						Issue Tracker
					</Typography>
					<Typography variant="body1" color="text.secondary">
						Report and track device issues
					</Typography>
				</Box>
				<Button
					variant="contained"
					startIcon={<Add />}
					onClick={() => setShowCreateDialog(true)}
				>
					Report Issue
				</Button>
			</Box>

			{issues.length > 0 && (
				<IssueFilterBar filters={filters} onChange={setFilters} />
			)}

			{issues.length === 0 ? (
				<Box
					display="flex"
					flexDirection="column"
					alignItems="center"
					justifyContent="center"
					py={8}
					gap={2}
				>
					<BugReport sx={{ fontSize: 64, color: 'text.secondary' }} />
					<Typography variant="h6">No issues reported yet</Typography>
					<Typography variant="body2" color="text.secondary" textAlign="center">
						Report device issues and attach images or files for faster resolution.
					</Typography>
					<Button
						variant="contained"
						startIcon={<Add />}
						onClick={() => setShowCreateDialog(true)}
					>
						Report Your First Issue
					</Button>
				</Box>
			) : filteredIssues.length === 0 ? (
				<Box
					display="flex"
					flexDirection="column"
					alignItems="center"
					justifyContent="center"
					py={8}
					gap={1}
				>
					<BugReport sx={{ fontSize: 48, color: 'text.secondary' }} />
					<Typography variant="h6">No issues match your filters</Typography>
					<Typography variant="body2" color="text.secondary">
						Try adjusting or clearing your filters.
					</Typography>
				</Box>
			) : (
				<Box display="flex" flexDirection="column" gap={2}>
					{filteredIssues.map((issue) => (
						<IssueCard
							key={issue.id}
							issue={issue}
							expanded={expandedIssue === issue.id}
							onToggleExpand={() =>
								setExpandedIssue(expandedIssue === issue.id ? null : issue.id)
							}
							onClose={() => handleCloseIssue(issue.id)}
							getAttachmentUrl={getAttachmentUrl}
							canClose={issue.status !== 'closed' && issue.status !== 'resolved'}
						/>
					))}
				</Box>
			)}

			<CreateIssueDialog
				open={showCreateDialog}
				onClose={() => setShowCreateDialog(false)}
				onSubmit={handleCreateIssue}
				sensors={sensors}
				loading={createLoading}
			/>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<Alert
					severity={snackbar.severity}
					onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
					variant="filled"
					sx={{ minWidth: 280 }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</PageContainer>
	)
}
